import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AuthTenantDto,
  type AuthUserDto,
  type PermissionKey,
  type SessionResponse,
  USER_ROLES,
} from '@ctms/contracts';

import { authApi } from './auth.api.js';
import { clearAuthToken, getAuthToken, setAuthToken, subscribeAuthToken } from './auth-token.js';
import { authQueryKeys } from './auth.query-keys.js';

export interface UseSessionResult {
  session: SessionResponse | undefined;
  user: AuthUserDto | undefined;
  tenant: AuthTenantDto | undefined;
  permissions: PermissionKey[];
  isLoading: boolean;
  isAuthenticated: boolean;
  isTenantAdmin: boolean;
  hasFinanceAccess: boolean;
  logout: () => void;
  setSessionToken: (token: string) => void;
  refetchSession: () => Promise<unknown>;
}

export function useSession(): UseSessionResult {
  const queryClient = useQueryClient();
  const [token, setTokenState] = useState<string | null>(() => getAuthToken());

  useEffect(() => {
    return subscribeAuthToken((newToken) => {
      setTokenState(newToken);
      if (!newToken) {
        queryClient.setQueryData(authQueryKeys.session(), null);
      }
    });
  }, [queryClient]);

  const sessionQuery = useQuery({
    queryKey: authQueryKeys.session(),
    queryFn: async (): Promise<SessionResponse> => {
      return authApi.fetchSession();
    },
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const logout = (): void => {
    clearAuthToken();
    queryClient.clear();
  };

  const setSessionToken = (newToken: string): void => {
    setAuthToken(newToken);
    void queryClient.invalidateQueries({ queryKey: authQueryKeys.session() });
  };

  const session = sessionQuery.data ?? undefined;
  const user = session?.user;
  const tenant = session?.tenant;
  const permissions = session?.permissions ?? [];

  const isTenantAdmin = user?.role === USER_ROLES.TENANT_ADMIN;
  const hasFinanceAccess = Boolean(user?.hasFinanceAccess) || isTenantAdmin;
  const isAuthenticated = Boolean(token && session);

  return {
    session,
    user,
    tenant,
    permissions,
    isLoading: Boolean(token) && sessionQuery.isLoading,
    isAuthenticated,
    isTenantAdmin,
    hasFinanceAccess,
    logout,
    setSessionToken,
    refetchSession: () => sessionQuery.refetch(),
  };
}
